import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { supabase } from '../services/supabase';
import PostRichContent from '../components/social/PostRichContent';

type GroupChatThreadMessage = {
  id: string;
  chatId: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string | null;
  content: string;
  type: string;
  timestamp: string;
  attachments: unknown[];
  isRead: boolean;
  isPinned?: boolean;
  pinnedAt?: string | null;
};

type GroupChatParticipant = {
  userId: string;
  fullName: string;
  avatar?: string | null;
  role?: string | null;
  joinedAt?: string | null;
  lastReadAt?: string | null;
};

type GroupChatThreadData = {
  chat: {
    id: string;
    groupId: string;
    name: string;
    description?: string | null;
    isPrivate: boolean;
    messageCount: number;
    createdBy?: string | null;
    updatedAt?: string | null;
  };
  viewerRole?: string | null;
  participants: GroupChatParticipant[];
  messages: GroupChatThreadMessage[];
  pinnedMessages: GroupChatThreadMessage[];
};

const THREAD_PAGE_SIZE = 100;

const Page = styled.div`
  min-height: 100%;
  padding: clamp(1rem, 2.5vw, 1.75rem);
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--primary) 12%, transparent), transparent 34%),
    linear-gradient(180deg, color-mix(in srgb, var(--surface) 90%, transparent) 0%, transparent 40%),
    var(--background);
`;

const Shell = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 0.9fr);
  gap: 1rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ThreadCard = styled.section`
  min-height: min(72vh, 860px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
`;

const ThreadTop = styled.div`
  display: grid;
  gap: 0;
`;

const PinnedBanner = styled.button`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  margin: 0;
  padding: 0.75rem 1.15rem;
  border: none;
  border-bottom: 1px solid color-mix(in srgb, var(--primary) 22%, var(--outline));
  background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  }
`;

const PinnedBannerLabel = styled.span`
  flex: 1;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--primary);
`;

const PinnedBannerChevron = styled.span`
  color: var(--muted);
  font-size: 0.9rem;
`;

const PinnedPanel = styled.div`
  margin: 0 1.15rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--primary) 20%, var(--outline));
  background: color-mix(in srgb, var(--surface) 92%, transparent);
  padding: 0.85rem;
  display: grid;
  gap: 0.65rem;
  max-height: 240px;
  overflow-y: auto;
`;

const PinnedListItem = styled.button`
  display: grid;
  gap: 0.2rem;
  width: 100%;
  text-align: left;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: color-mix(in srgb, var(--surface) 88%, transparent);
  padding: 0.7rem 0.8rem;
  font: inherit;
  cursor: pointer;
  color: var(--text);

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 30%, var(--outline));
  }
`;

const MessageActions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const MessageActionButton = styled.button`
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 80%, transparent);
  color: var(--muted);
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const PinnedMessageMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--primary);
`;

const SidebarCard = styled.aside`
  display: grid;
  gap: 1rem;
  align-content: start;
`;

const Panel = styled.section`
  border-radius: 24px;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  padding: 1rem;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
`;

const Header = styled.div`
  display: grid;
  gap: 0.9rem;
  padding: 1.15rem 1.15rem 1rem;
  border-bottom: 1px solid var(--outline);
  background: linear-gradient(180deg, color-mix(in srgb, var(--primary) 9%, transparent), transparent);
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const Eyebrow = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--primary);
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.45rem, 2.6vw, 2rem);
  line-height: 1.1;
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
`;

const BackButton = styled.button`
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 78%, transparent);
  color: var(--text);
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Badge = styled.span<{ $tone?: 'primary' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  padding: 0.42rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'primary'
      ? 'color-mix(in srgb, var(--primary) 16%, transparent)'
      : 'color-mix(in srgb, var(--surface) 80%, transparent)'};
  border: 1px solid
    ${({ $tone }) => ($tone === 'primary' ? 'color-mix(in srgb, var(--primary) 30%, var(--outline))' : 'var(--outline)')};
  color: ${({ $tone }) => ($tone === 'primary' ? 'var(--primary)' : 'var(--muted)')};
`;

const StatusBanner = styled.div<{ $tone?: 'error' | 'info' }>`
  margin: 0 1.15rem;
  border-radius: 16px;
  border: 1px solid
    ${({ $tone }) => ($tone === 'error' ? 'rgba(220, 38, 38, 0.24)' : 'rgba(14, 165, 233, 0.24)')};
  background: ${({ $tone }) => ($tone === 'error' ? 'rgba(220, 38, 38, 0.08)' : 'rgba(14, 165, 233, 0.08)')};
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--error)' : 'var(--text)')};
  padding: 0.8rem 0.95rem;
  line-height: 1.45;
`;

const MessagesViewport = styled.div`
  min-height: 0;
  padding: 1rem 1.15rem;
  overflow-y: auto;
  display: grid;
  align-content: start;
  gap: 0.9rem;
  background:
    linear-gradient(180deg, transparent 0%, color-mix(in srgb, var(--background) 18%, transparent) 100%),
    color-mix(in srgb, var(--surface) 88%, transparent);
`;

const EmptyState = styled.div`
  border: 1px dashed var(--outline);
  border-radius: 18px;
  padding: 1rem;
  color: var(--muted);
  background: color-mix(in srgb, var(--surface) 76%, transparent);
`;

const MessageRow = styled.div<{ $isCurrentUser: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isCurrentUser }) => ($isCurrentUser ? '1fr' : '44px 1fr')};
  justify-items: ${({ $isCurrentUser }) => ($isCurrentUser ? 'end' : 'stretch')};
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--primary) 24%, transparent), color-mix(in srgb, var(--accent, var(--primary)) 28%, transparent));
  color: var(--primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.9rem;
`;

const BubbleWrap = styled.div<{ $isCurrentUser: boolean }>`
  max-width: min(100%, 640px);
  display: grid;
  gap: 0.35rem;
  justify-items: ${({ $isCurrentUser }) => ($isCurrentUser ? 'end' : 'start')};
`;

const SenderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.76rem;
  color: var(--muted);
`;

const SenderName = styled.span`
  font-weight: 700;
  color: var(--text);
`;

const MessageBubble = styled.div<{ $isCurrentUser: boolean }>`
  width: fit-content;
  max-width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  padding: 0.9rem 1rem;
  border-radius: ${({ $isCurrentUser }) => ($isCurrentUser ? '20px 20px 8px 20px' : '20px 20px 20px 8px')};
  border: 1px solid ${({ $isCurrentUser }) => ($isCurrentUser ? 'transparent' : 'var(--outline)')};
  background: ${({ $isCurrentUser }) =>
    $isCurrentUser
      ? 'linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 70%, black))'
      : 'color-mix(in srgb, var(--surface) 96%, transparent)'};
  color: ${({ $isCurrentUser }) => ($isCurrentUser ? 'var(--onPrimary)' : 'var(--text)')};
  box-shadow: ${({ $isCurrentUser }) => ($isCurrentUser ? '0 16px 32px rgba(15, 23, 42, 0.18)' : 'none')};
`;

const AttachmentsNote = styled.div`
  font-size: 0.72rem;
  color: var(--muted);
`;

const Composer = styled.form`
  display: grid;
  gap: 0.8rem;
  padding: 1rem 1.15rem 1.15rem;
  border-top: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 96%, transparent);
`;

const ComposerLabel = styled.label`
  display: grid;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 700;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  border-radius: 18px;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--background) 74%, transparent);
  color: var(--text);
  padding: 0.95rem 1rem;
  font: inherit;
  line-height: 1.5;
`;

const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ComposerHint = styled.span`
  font-size: 0.78rem;
  color: var(--muted);
`;

const SendButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.8rem 1.15rem;
  min-width: 132px;
  background: linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 78%, black));
  color: var(--onPrimary);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
`;

const DetailList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const DetailItem = styled.div`
  display: grid;
  gap: 0.22rem;
`;

const DetailLabel = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

const DetailValue = styled.span`
  line-height: 1.5;
  color: var(--text);
`;

const ParticipantList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const ParticipantRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ParticipantMeta = styled.div`
  display: grid;
  gap: 0.15rem;
`;

const ParticipantName = styled.span`
  font-weight: 700;
  color: var(--text);
`;

const ParticipantRole = styled.span`
  font-size: 0.8rem;
  color: var(--muted);
`;

const LoadingState = styled(EmptyState)`
  margin: 0 auto;
  max-width: 1120px;
`;

const normalizeMessage = (value: any): GroupChatThreadMessage => ({
  id: String(value?.id ?? ''),
  chatId: String(value?.chat_id ?? value?.chatId ?? ''),
  senderId: String(value?.sender_id ?? value?.senderId ?? ''),
  senderName: String(value?.sender?.full_name ?? value?.sender_name ?? value?.senderName ?? 'Member'),
  senderAvatar: value?.sender?.avatar ?? value?.sender_avatar ?? value?.senderAvatar ?? null,
  content: String(value?.content ?? ''),
  type: String(value?.type ?? 'text'),
  timestamp: String(value?.timestamp ?? new Date().toISOString()),
  attachments: Array.isArray(value?.attachments) ? value.attachments : [],
  isRead: Boolean(value?.is_read ?? value?.isRead),
  isPinned: Boolean(value?.is_pinned ?? value?.isPinned),
  pinnedAt: value?.pinned_at ?? value?.pinnedAt ?? null,
});

const normalizeParticipant = (value: any): GroupChatParticipant => ({
  userId: String(value?.user_id ?? value?.userId ?? ''),
  fullName: String(value?.full_name ?? value?.user_name ?? value?.fullName ?? 'Member'),
  avatar: value?.avatar ?? value?.user_avatar ?? value?.userAvatar ?? null,
  role: value?.role ?? null,
  joinedAt: value?.joined_at ?? value?.joinedAt ?? null,
  lastReadAt: value?.last_read_at ?? value?.lastReadAt ?? null,
});

const normalizeThreadData = (value: any): GroupChatThreadData => ({
  chat: {
    id: String(value?.chat?.id ?? value?.id ?? ''),
    groupId: String(value?.chat?.group_id ?? value?.group_id ?? ''),
    name: String(value?.chat?.name ?? value?.name ?? 'Group chat'),
    description: value?.chat?.description ?? value?.description ?? null,
    isPrivate: Boolean(value?.chat?.is_private ?? value?.is_private),
    messageCount: Number(value?.chat?.message_count ?? value?.message_count ?? 0),
    createdBy: value?.chat?.created_by ?? value?.created_by ?? null,
    updatedAt: value?.chat?.updated_at ?? value?.updated_at ?? null,
  },
  viewerRole: value?.viewer_role ?? null,
  participants: Array.isArray(value?.participants) ? value.participants.map(normalizeParticipant) : [],
  messages: Array.isArray(value?.messages) ? value.messages.map(normalizeMessage) : [],
  pinnedMessages: Array.isArray(value?.pinned_messages)
    ? value.pinned_messages.map(normalizeMessage)
    : Array.isArray(value?.pinnedMessages)
      ? value.pinnedMessages.map(normalizeMessage)
      : [],
});

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'object' && error && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim();
    if (message) return message;
  }

  return fallback;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown time';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown time';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const getInitials = (value: string) => {
  const parts = value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return 'GM';
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
};

const CommunityGroupChatThreadPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const endRef = useRef<HTMLDivElement | null>(null);
  const { groupId = '', chatId = '' } = useParams<{ groupId: string; chatId: string }>();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<GroupChatThreadMessage[]>([]);
  const [pinnedMessages, setPinnedMessages] = useState<GroupChatThreadMessage[]>([]);
  const [showPinnedList, setShowPinnedList] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [pinningMessageId, setPinningMessageId] = useState<string | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const senderName = useMemo(() => {
    const resolved = profile?.full_name?.trim() || user?.user_metadata?.full_name || user?.email || 'Member';
    return String(resolved).trim() || 'Member';
  }, [profile?.full_name, user?.email, user?.user_metadata?.full_name]);

  const threadQuery = useQuery<GroupChatThreadData>({
    queryKey: ['community-group-chat-thread', groupId, chatId],
    enabled: Boolean(user?.id && groupId && chatId),
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('get_group_chat_bootstrap', {
          p_chat_id: chatId,
          p_limit: THREAD_PAGE_SIZE,
          p_offset: 0,
        });

        if (error) throw error;
        return normalizeThreadData(data);
      } catch (rpcError) {
        const [
          { data: chatRow, error: chatError },
          { data: membershipRow, error: membershipError },
          { data: participantRows, error: participantError },
          { data: messageRows, error: messageError },
          { data: pinnedRows, error: pinnedError },
        ] = await Promise.all([
          supabase
            .from('group_chats')
            .select('id, group_id, name, description, is_private, message_count, created_by, updated_at')
            .eq('id', chatId)
            .eq('group_id', groupId)
            .single(),
          supabase
            .from('group_members')
            .select('role')
            .eq('group_id', groupId)
            .eq('user_id', user?.id ?? '')
            .maybeSingle(),
          supabase
            .from('group_chat_participants')
            .select('user_id, joined_at, last_read_at, user_name, user_avatar')
            .eq('chat_id', chatId)
            .order('joined_at', { ascending: true }),
          supabase
            .from('group_chat_messages')
            .select(`
              id,
              chat_id,
              sender_id,
              sender_name,
              content,
              type,
              attachments,
              timestamp,
              is_read,
              is_pinned,
              pinned_at,
              sender:sender_id (
                id,
                full_name,
                avatar
              )
            `)
            .eq('chat_id', chatId)
            .is('hidden_at', null)
            .is('deleted_at', null)
            .order('timestamp', { ascending: true })
            .limit(THREAD_PAGE_SIZE),
          supabase
            .from('group_chat_messages')
            .select(`
              id,
              chat_id,
              sender_id,
              sender_name,
              content,
              type,
              attachments,
              timestamp,
              is_read,
              is_pinned,
              pinned_at,
              sender:sender_id (
                id,
                full_name,
                avatar
              )
            `)
            .eq('chat_id', chatId)
            .eq('is_pinned', true)
            .is('hidden_at', null)
            .is('deleted_at', null)
            .order('pinned_at', { ascending: false, nullsFirst: false })
            .order('timestamp', { ascending: false }),
        ]);

        if (chatError) throw chatError;
        if (membershipError) throw membershipError;
        if (participantError) throw participantError;
        if (messageError) throw messageError;
        if (pinnedError) throw pinnedError;

        if (!chatRow) {
          throw rpcError;
        }

        return normalizeThreadData({
          chat: chatRow,
          viewer_role: membershipRow?.role ?? null,
          participants: participantRows ?? [],
          messages: messageRows ?? [],
          pinned_messages: pinnedRows ?? [],
        });
      }
    },
  });

  useEffect(() => {
    setMessages(threadQuery.data?.messages ?? []);
  }, [threadQuery.data?.messages]);

  useEffect(() => {
    const fromBootstrap = threadQuery.data?.pinnedMessages ?? [];
    if (fromBootstrap.length > 0) {
      setPinnedMessages(fromBootstrap);
      return;
    }
    setPinnedMessages((threadQuery.data?.messages ?? []).filter((message) => message.isPinned));
  }, [threadQuery.data?.messages, threadQuery.data?.pinnedMessages]);

  useEffect(() => {
    if (!chatId) return undefined;

    const channel = supabase
      .channel(`community-group-chat-thread-${chatId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'group_chat_messages', filter: `chat_id=eq.${chatId}` },
        (payload) => {
          const nextMessage = normalizeMessage(payload.new);
          setMessages((current) => {
            if (current.some((message) => message.id === nextMessage.id)) {
              return current;
            }
            return [...current, nextMessage];
          });
          void queryClient.invalidateQueries({ queryKey: ['community-group-chats', groupId] });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [chatId, groupId, queryClient]);

  useEffect(() => {
    if (typeof endRef.current?.scrollIntoView === 'function') {
      endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || !chatId || !user?.id) return;

    setIsSending(true);
    setSendError(null);

    try {
      let sentMessage: GroupChatThreadMessage | null = null;

      try {
        const { data, error } = await supabase.rpc('send_group_chat_message_txn', {
          p_chat_id: chatId,
          p_sender_id: user.id,
          p_sender_name: senderName,
          p_content: content,
          p_type: 'text',
          p_attachments: [],
        });

        if (error) throw error;
        if (data) {
          sentMessage = normalizeMessage(data);
        }
      } catch (rpcError) {
        const { data, error } = await supabase
          .from('group_chat_messages')
          .insert({
            chat_id: chatId,
            sender_id: user.id,
            sender_name: senderName,
            content,
            type: 'text',
            attachments: [],
            timestamp: new Date().toISOString(),
            is_read: false,
          })
          .select(`
            id,
            chat_id,
            sender_id,
            sender_name,
            content,
            type,
            attachments,
            timestamp,
            is_read,
            sender:sender_id (
              id,
              full_name,
              avatar
            )
          `)
          .single();

        if (error) throw error;

        await supabase
          .from('group_chats')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', chatId);

        sentMessage = normalizeMessage(data ?? rpcError);
      }

      if (sentMessage) {
        setMessages((current) => {
          if (current.some((message) => message.id === sentMessage?.id)) {
            return current;
          }
          return [...current, sentMessage as GroupChatThreadMessage];
        });
      }

      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['community-group-chat-thread', groupId, chatId] });
      void queryClient.invalidateQueries({ queryKey: ['community-group-chats', groupId] });
    } catch (error) {
      setSendError(getErrorMessage(error, 'Failed to send message.'));
    } finally {
      setIsSending(false);
    }
  };

  const handleComposerKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await handleSend();
    }
  };

  const scrollToMessage = (messageId: string) => {
    const node = messageRefs.current[messageId];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleToggleMessagePinned = async (message: GroupChatThreadMessage) => {
    const nextPinned = !Boolean(message.isPinned);
    setPinningMessageId(message.id);
    setSendError(null);

    setMessages((current) =>
      current.map((entry) =>
        entry.id === message.id
          ? { ...entry, isPinned: nextPinned, pinnedAt: nextPinned ? new Date().toISOString() : null }
          : entry
      )
    );
    setPinnedMessages((current) => {
      if (nextPinned) {
        const without = current.filter((entry) => entry.id !== message.id);
        return [{ ...message, isPinned: true, pinnedAt: new Date().toISOString() }, ...without];
      }
      return current.filter((entry) => entry.id !== message.id);
    });

    const { error } = await supabase.rpc('set_group_chat_message_pinned', {
      p_message_id: message.id,
      p_pinned: nextPinned,
    });

    setPinningMessageId(null);
    if (error) {
      setSendError(getErrorMessage(error, 'Failed to update pin.'));
      void queryClient.invalidateQueries({ queryKey: ['community-group-chat-thread', groupId, chatId] });
    }
  };

  const handleDeleteMessage = async (message: GroupChatThreadMessage) => {
    if (!window.confirm('Delete this message?')) return;

    setDeletingMessageId(message.id);
    setSendError(null);
    const { error } = await supabase
      .from('group_chat_messages')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', message.id);

    setDeletingMessageId(null);
    if (error) {
      setSendError(getErrorMessage(error, 'Failed to delete message.'));
      return;
    }

    setMessages((current) => current.filter((entry) => entry.id !== message.id));
    setPinnedMessages((current) => current.filter((entry) => entry.id !== message.id));
  };

  if (!groupId || !chatId) {
    return (
      <Page>
        <LoadingState>Missing group or chat route parameters.</LoadingState>
      </Page>
    );
  }

  if (threadQuery.isLoading) {
    return (
      <Page>
        <LoadingState>Loading chat thread...</LoadingState>
      </Page>
    );
  }

  if (threadQuery.error || !threadQuery.data) {
    return (
      <Page>
        <LoadingState>{getErrorMessage(threadQuery.error, 'Unable to load this chat thread.')}</LoadingState>
      </Page>
    );
  }

  const { chat, participants, viewerRole } = threadQuery.data;
  const resolvedGroupId = chat.groupId || groupId;
  const canManageChat =
    viewerRole === 'admin' || viewerRole === 'moderator' || chat.createdBy === user?.id;

  return (
    <Page>
      <Shell>
        <ThreadCard>
          <ThreadTop>
            <Header>
              <TopRow>
                <HeaderCopy>
                  <Eyebrow>Group chat</Eyebrow>
                  <Title>{chat.name}</Title>
                  <Subtitle>{chat.description?.trim() || 'Stay in sync with this group conversation in real time.'}</Subtitle>
                </HeaderCopy>
                <BackButton type="button" onClick={() => navigate(`/groups/${resolvedGroupId}?tab=chats`)}>
                  Back to group
                </BackButton>
              </TopRow>
              <BadgeRow>
                <Badge $tone="primary">{chat.isPrivate ? 'Private chat' : 'Open chat'}</Badge>
                <Badge>{participants.length} participants</Badge>
                <Badge>{messages.length} loaded messages</Badge>
                {canManageChat ? <Badge>Pin and delete controls available</Badge> : null}
              </BadgeRow>
            </Header>

            {pinnedMessages.length > 0 ? (
              <PinnedBanner
                type="button"
                onClick={() => setShowPinnedList((current) => !current)}
                aria-expanded={showPinnedList}
              >
                <PinnedBannerLabel>
                  {pinnedMessages.length} Pinned Message{pinnedMessages.length === 1 ? '' : 's'}
                </PinnedBannerLabel>
                <PinnedBannerChevron>{showPinnedList ? 'Hide' : 'View'}</PinnedBannerChevron>
              </PinnedBanner>
            ) : null}

            {showPinnedList && pinnedMessages.length > 0 ? (
              <PinnedPanel>
                {pinnedMessages.map((pinned) => (
                  <PinnedListItem
                    key={pinned.id}
                    type="button"
                    onClick={() => {
                      setShowPinnedList(false);
                      scrollToMessage(pinned.id);
                    }}
                  >
                    <strong>{pinned.senderName}</strong>
                    <span>{pinned.content || (pinned.type === 'image' ? 'Image' : 'Message')}</span>
                  </PinnedListItem>
                ))}
              </PinnedPanel>
            ) : null}

            {sendError ? <StatusBanner $tone="error">{sendError}</StatusBanner> : null}
          </ThreadTop>

          <MessagesViewport>
            {messages.length ? (
              messages.map((message) => {
                const isCurrentUser = message.senderId === user?.id;
                return (
                  <div
                    key={message.id}
                    ref={(node) => {
                      messageRefs.current[message.id] = node;
                    }}
                  >
                    <MessageRow $isCurrentUser={isCurrentUser}>
                      {isCurrentUser ? null : <Avatar aria-hidden="true">{getInitials(message.senderName)}</Avatar>}
                      <BubbleWrap $isCurrentUser={isCurrentUser}>
                        <SenderMeta>
                          <SenderName>{isCurrentUser ? 'You' : message.senderName}</SenderName>
                          {message.isPinned ? <PinnedMessageMark>Pinned</PinnedMessageMark> : null}
                          <span>{formatTimestamp(message.timestamp)}</span>
                        </SenderMeta>
                        <MessageBubble $isCurrentUser={isCurrentUser}>
                          {message.content ? (
                            <PostRichContent text={message.content} compact />
                          ) : (
                            'No message content.'
                          )}
                        </MessageBubble>
                        {message.attachments.length ? <AttachmentsNote>{message.attachments.length} attachment(s)</AttachmentsNote> : null}
                        {canManageChat ? (
                          <MessageActions>
                            <MessageActionButton
                              type="button"
                              disabled={pinningMessageId === message.id}
                              onClick={() => void handleToggleMessagePinned(message)}
                            >
                              {pinningMessageId === message.id
                                ? 'Working...'
                                : message.isPinned
                                  ? 'Unpin'
                                  : 'Pin'}
                            </MessageActionButton>
                            <MessageActionButton
                              type="button"
                              disabled={deletingMessageId === message.id}
                              onClick={() => void handleDeleteMessage(message)}
                            >
                              {deletingMessageId === message.id ? 'Deleting...' : 'Delete'}
                            </MessageActionButton>
                          </MessageActions>
                        ) : null}
                      </BubbleWrap>
                    </MessageRow>
                  </div>
                );
              })
            ) : (
              <EmptyState>No messages yet. Start the conversation here.</EmptyState>
            )}
            <div ref={endRef} />
          </MessagesViewport>

          <Composer
            onSubmit={(event) => {
              event.preventDefault();
              void handleSend();
            }}
          >
            <ComposerLabel>
              Message
              <TextArea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => void handleComposerKeyDown(event)}
                placeholder="Write a message to the group chat"
              />
            </ComposerLabel>
            <ComposerFooter>
              <ComposerHint>Press Enter to send. Use Shift+Enter for a new line.</ComposerHint>
              <SendButton type="submit" disabled={isSending || !draft.trim()}>
                {isSending ? 'Sending...' : 'Send message'}
              </SendButton>
            </ComposerFooter>
          </Composer>
        </ThreadCard>

        <SidebarCard>
          <Panel>
            <SectionTitle>Thread details</SectionTitle>
            <DetailList>
              <DetailItem>
                <DetailLabel>Visibility</DetailLabel>
                <DetailValue>{chat.isPrivate ? 'Private group chat' : 'Available to all group members'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Last updated</DetailLabel>
                <DetailValue>{formatTimestamp(chat.updatedAt)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Your role</DetailLabel>
                <DetailValue>{viewerRole ? viewerRole.replace('_', ' ') : 'Member'}</DetailValue>
              </DetailItem>
            </DetailList>
          </Panel>

          <Panel>
            <SectionTitle>Participants</SectionTitle>
            {participants.length ? (
              <ParticipantList>
                {participants.map((participant) => (
                  <ParticipantRow key={participant.userId}>
                    <Avatar aria-hidden="true">{getInitials(participant.fullName)}</Avatar>
                    <ParticipantMeta>
                      <ParticipantName>{participant.fullName}</ParticipantName>
                      <ParticipantRole>{participant.role ? participant.role.replace('_', ' ') : 'Member'}</ParticipantRole>
                    </ParticipantMeta>
                  </ParticipantRow>
                ))}
              </ParticipantList>
            ) : (
              <EmptyState>No participant list is available for this thread.</EmptyState>
            )}
          </Panel>
        </SidebarCard>
      </Shell>
    </Page>
  );
};

export default CommunityGroupChatThreadPage;
